/**
 * sectionWords — the screen says "Section 34", never "§34".
 *
 * Founder, 18 Oct 2026, on the tax computation's "Late fees on PF and TDS ·
 * ₹6,471.00 §34 (a penalty or ...)": the § mark is not how an Indian reader
 * writes a section of the Act, and it showed all over Karr.
 *
 * The mark comes from three places: our own strings, text the database writes
 * (the tax computation's reasons are built in SQL), and edge functions. Editing
 * each at its source would also change values that are stored, filtered on and
 * compared as keys. So the words are fixed where they are drawn: one observer
 * rewrites the text nodes (and title / placeholder / aria-label) that carry the
 * mark. The data is never touched, and a box the person types into (input,
 * textarea, contenteditable) is left as typed.
 *
 *   §34          -> Section 34
 *   §36(4)       -> Section 36(4)
 *   §§ 34-37     -> Sections 34-37
 *   §185–188     -> Sections 185–188
 */

const MARK = '§';

export function sectionWords(text: string): string {
    if (!text.includes(MARK)) return text;
    return text
        .replace(/§§\s*/g, 'Sections ')
        // a range: §185–188, §34-37, §36(4)–(6) reads as plural
        .replace(/§\s*(?=\d+[A-Z]{0,3}(?:\([0-9A-Za-z]+\))*\s*[–-]\s*[\d(])/g, 'Sections ')
        .replace(/§\s*(?=\S)/g, 'Section ')
        .replace(/§/g, 'Section');
}

const SKIP = new Set(['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA', 'INPUT', 'CODE', 'PRE']);
const ATTRS = ['title', 'placeholder', 'aria-label'] as const;
const ATTR_SELECTOR = ATTRS.map((a) => `[${a}*="${MARK}"]`).join(',');

function leaveAlone(el: Element | null): boolean {
    if (!el) return false;
    if (SKIP.has(el.tagName)) return true;
    return (el as HTMLElement).isContentEditable === true;
}

function fixText(node: Text): void {
    const v = node.data;
    if (!v.includes(MARK) || leaveAlone(node.parentElement)) return;
    const next = sectionWords(v);
    if (next !== v) node.data = next;
}

function fixAttrs(el: Element): void {
    if (leaveAlone(el) && el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    for (const a of ATTRS) {
        const v = el.getAttribute(a);
        if (v && v.includes(MARK)) el.setAttribute(a, sectionWords(v));
    }
}

/** Rewrite every § under `root`; cheap when there is none (one textContent read). */
export function fixSectionMarks(root: Node): void {
    if (root.nodeType === Node.TEXT_NODE) {
        fixText(root as Text);
        return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_NODE) return;
    const el = root as Element;
    if (el.nodeType === Node.ELEMENT_NODE) fixAttrs(el);
    el.querySelectorAll?.(ATTR_SELECTOR).forEach(fixAttrs);
    if (!(root.textContent ?? '').includes(MARK)) return;
    const doc = root.ownerDocument ?? (root as Document);
    const walker = doc.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
        acceptNode: (n) => ((n as Text).data.includes(MARK) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_SKIP),
    });
    const hits: Text[] = [];
    for (let n = walker.nextNode(); n; n = walker.nextNode()) hits.push(n as Text);
    hits.forEach(fixText);
}

let installed: MutationObserver | null = null;

/** Called once from main.tsx; returns the stop function (tests). */
export function installSectionWords(doc: Document = document): () => void {
    if (installed) return () => {};
    const root = doc.documentElement;
    fixSectionMarks(root);
    const obs = new MutationObserver((records) => {
        for (const r of records) {
            if (r.type === 'characterData') fixText(r.target as Text);
            else if (r.type === 'attributes') fixAttrs(r.target as Element);
            else r.addedNodes.forEach(fixSectionMarks);
        }
    });
    obs.observe(root, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: [...ATTRS],
    });
    installed = obs;
    return () => {
        obs.disconnect();
        installed = null;
    };
}
